/**
 * Registry for persisted store instances: used to purge storage on logout
 * (via persist.clearStorage()) and by PersistGate to wait for hydration.
 */

/**
 * Minimal store API shape for Zustand persist middleware.
 * Zustand does not export this type; the full shape is StorePersist in zustand/middleware/persist.d.ts.
 */
export interface PersistStoreApi {
  persist?: {
    clearStorage: () => void;
    hasHydrated: () => boolean;
    onFinishHydration: (cb: () => void) => () => void;
  };
}

interface RegisteredPersistStore {
  store: PersistStoreApi;
  purgeOnLogout: boolean;
}

const persistedStores: RegisteredPersistStore[] = [];

/**
 * Register a persisted store. Called by createAppStore when a store with persist options is created.
 * @param store - The Zustand store (must have .persist from persist middleware)
 * @param options - Optional. purgeOnLogout (default true) controls whether this store is cleared on logout.
 */
export const registerPersistStore = (
  store: PersistStoreApi,
  options?: { purgeOnLogout?: boolean },
) => {
  if (!store?.persist) return;
  const alreadyRegistered = persistedStores.some((entry) => entry.store === store);

  if (!alreadyRegistered) {
    persistedStores.push({
      store,
      purgeOnLogout: options?.purgeOnLogout ?? true,
    });
  }
};

/**
 * Return all registered persisted stores. Used by PersistGate and by purge.
 * Import persisted-stores.ts before using so that store modules have been loaded and registered.
 */
export const getRegisteredPersistStores = (): PersistStoreApi[] =>
  persistedStores.map((entry) => entry.store);

/**
 * Clear persisted state for registered stores that have purgeOnLogout true. Calls persist.clearStorage() on each.
 * Should be called during logout to clean up user-specific persisted state.
 */
export const purgeRegisteredStores = () => {
  persistedStores.forEach(({ store, purgeOnLogout }) => {
    if (purgeOnLogout) {
      store.persist?.clearStorage();
    }
  });
};
